/**
 * Keeps a Kanban card in the column it was dropped on while the stage change
 * waits on a confirmation dialog (send interview, accept, reject, hire) and
 * while the confirmed action is in flight.
 *
 * Cancel, or a failure once the dialog is closed, drops the override so the
 * card returns to its origin. Confirm endpoints append to the destination
 * stage, so success persists the drop position through `reorderCard`, whose
 * optimistic write replaces the override.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Candidate, PipelineStage } from '../models/job';
import {
  applyOptimisticCardMove,
  type OptimisticCardMove,
} from '../utils/pipeline-cache';
import type { CandidatePipelineMutations } from './useCandidatePipelineMutations';
import type { useCandidatePipelineDnd } from './useCandidatePipelineDnd';
import type { CandidateStageMoveRequestOptions } from './useCandidateStageMove';

interface PendingStageMove extends OptimisticCardMove {
  isSubmitting: boolean;
  isConfirmed: boolean;
}

export function useCandidatePendingStageMove(
  mutations: CandidatePipelineMutations,
  pipelineDnd: Pick<ReturnType<typeof useCandidatePipelineDnd>, 'reorderCard'>,
  candidates: Candidate[]
) {
  const [pending, setPending] = useState<PendingStageMove | null>(null);

  const isDialogOpen =
    mutations.showSendInterviewDialog ||
    mutations.showAcceptDialog ||
    mutations.showRejectDialog ||
    mutations.showHireDialog;
  const isActive =
    pending != null &&
    (pending.isConfirmed || pending.isSubmitting || isDialogOpen);

  useEffect(() => {
    if (pending && !isActive) setPending(null);
  }, [pending, isActive]);

  useEffect(() => {
    setPending((current) => (current?.isConfirmed ? null : current));
  }, [candidates]);

  const boardCandidates = useMemo(() => {
    if (!isActive || !pending) return candidates;
    return applyOptimisticCardMove(candidates, pending) ?? candidates;
  }, [candidates, pending, isActive]);

  const requestMoveToStage = useCallback(
    (
      candidate: Candidate,
      targetStage: PipelineStage,
      options: CandidateStageMoveRequestOptions = {}
    ) => {
      const isOpened = mutations.requestMoveToStage(candidate, targetStage);
      if (isOpened) {
        setPending({
          candidateId: candidate.id,
          toStageId: targetStage.id,
          toStageType: targetStage.stageType,
          insertAfterCandidateId: options.insertAfterCandidateId,
          isSubmitting: false,
          isConfirmed: false,
        });
      }
      return isOpened;
    },
    [mutations]
  );

  const trackConfirm =
    <TArgs extends unknown[], TResult>(
      action: (...args: TArgs) => Promise<TResult>
    ) =>
    async (...args: TArgs): Promise<TResult> => {
      const candidateId = mutations.activeCandidate?.id;
      const move = pending?.candidateId === candidateId ? pending : null;
      const update = (patch: Partial<PendingStageMove>) =>
        setPending((current) =>
          current && current.candidateId === candidateId
            ? { ...current, ...patch }
            : current
        );
      update({ isSubmitting: true });
      const result = await action(...args);
      update({ isSubmitting: false, isConfirmed: result === true });
      if (result === true && move && move.insertAfterCandidateId !== undefined) {
        pipelineDnd.reorderCard.mutate({
          candidateId: move.candidateId,
          stageId: move.toStageId,
          stageType: move.toStageType,
          insertAfterCandidateId: move.insertAfterCandidateId,
        });
      }
      return result;
    };

  return {
    candidates: boardCandidates,
    mutations: {
      ...mutations,
      requestMoveToStage,
      handleAccept: trackConfirm(mutations.handleAccept),
      confirmReject: trackConfirm(mutations.confirmReject),
      confirmHire: trackConfirm(mutations.confirmHire),
      confirmSendInterview: trackConfirm(mutations.confirmSendInterview),
    },
  };
}
